import React, { useEffect } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeCookie = 'indigo-toggle-dark';
let themeCookieExpiry = 90; // days

const AddDarkTheme = () => {
  const cookies = new Cookies();
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + themeCookieExpiry);
  };

  const getCookieOptions = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const options = { domain: serverURL.hostname, path: '/', expires: getCookieExpiry() };
    return options;
  };

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[index].contentDocument) { iframes[index].contentDocument.head.appendChild(style); }
      });
    }
  };

  useEffect(() => {
    const theme = cookies.get(themeCookie);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.body.classList.add('indigo-dark-theme');
      
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage

      cookies.set(themeCookie, theme, getCookieOptions());      //  on page load, update expiry
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
        
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {
  const { getAuthenticatedUser } = await import('@edx/frontend-platform/auth');
  const { getConfig } = await import('@edx/frontend-platform');
  const { CustomDesktopNav, CustomMobileNav, UserProfileBootstrap, FloatingButtons } = await import('custom-slot-components');
  const { Helmet } = await import('react-helmet');

  let CustomDesktopNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "authoring" === "authoring" ? null : "authoring";
      return <CustomDesktopNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let CustomMobileNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "authoring" === "authoring" ? null : "authoring";
      return <CustomMobileNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let FloatingButtonsWrapper = (props) => {
    const user = getAuthenticatedUser();
    const platformConfig = getConfig();
    return <FloatingButtons
      user={user}
      config={platformConfig}
      backToTop
      {...props}
    />
  };

  const headerScripts = () => {
    return (
      <Helmet>
        <script type="text/javascript">
          {`
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'KTGKT';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);
`}
        </script>
      </Helmet>
    )
  };
             
      addPlugins(config, 'desktop_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_desktop",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_desktop_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomDesktopNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'mobile_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_mobile",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_mobile_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomMobileNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'helmet_slot', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'helmet_slot',
              type: DIRECT_PLUGIN,
              RenderWidget: headerScripts,
            },
          }
          ]);
    }
    if (process.env.APP_ID == 'account') {
  const { getAuthenticatedUser } = await import('@edx/frontend-platform/auth');
  const { getConfig } = await import('@edx/frontend-platform');
  const { CustomDesktopNav, CustomMobileNav, UserProfileBootstrap, FloatingButtons } = await import('custom-slot-components');
  const { Helmet } = await import('react-helmet');

  let CustomDesktopNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "account" === "authoring" ? null : "account";
      return <CustomDesktopNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let CustomMobileNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "account" === "authoring" ? null : "account";
      return <CustomMobileNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let FloatingButtonsWrapper = (props) => {
    const user = getAuthenticatedUser();
    const platformConfig = getConfig();
    return <FloatingButtons
      user={user}
      config={platformConfig}
      backToTop
      {...props}
    />
  };

  const headerScripts = () => {
    return (
      <Helmet>
        <script type="text/javascript">
          {`
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'KTGKT';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);
`}
        </script>
      </Helmet>
    )
  };
             
      addPlugins(config, 'desktop_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_desktop",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_desktop_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomDesktopNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'mobile_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_mobile",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_mobile_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomMobileNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'helmet_slot', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'helmet_slot',
              type: DIRECT_PLUGIN,
              RenderWidget: headerScripts,
            },
          }
          ]);
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
  const { getAuthenticatedUser } = await import('@edx/frontend-platform/auth');
  const { getConfig } = await import('@edx/frontend-platform');
  const { CustomDesktopNav, CustomMobileNav, UserProfileBootstrap, FloatingButtons } = await import('custom-slot-components');
  const { Helmet } = await import('react-helmet');

  let CustomDesktopNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "learner-dashboard" === "authoring" ? null : "learner-dashboard";
      return <CustomDesktopNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let CustomMobileNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "learner-dashboard" === "authoring" ? null : "learner-dashboard";
      return <CustomMobileNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let FloatingButtonsWrapper = (props) => {
    const user = getAuthenticatedUser();
    const platformConfig = getConfig();
    return <FloatingButtons
      user={user}
      config={platformConfig}
      backToTop
      {...props}
    />
  };

  const headerScripts = () => {
    return (
      <Helmet>
        <script type="text/javascript">
          {`
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'KTGKT';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);
`}
        </script>
      </Helmet>
    )
  };
             

      FloatingButtonsWrapper = (props) => {
        const user = getAuthenticatedUser();
        const platformConfig = getConfig();

        return (
          <FloatingButtons
            user={user}
            config={platformConfig}
            phoenixAssistant
            {...props}
          />
        );
      };

          
      addPlugins(config, 'desktop_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_desktop",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_desktop_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomDesktopNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'mobile_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_mobile",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_mobile_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomMobileNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'helmet_slot', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'helmet_slot',
              type: DIRECT_PLUGIN,
              RenderWidget: headerScripts,
            },
          }
          ]);
    }
    if (process.env.APP_ID == 'learning') {
  const { getAuthenticatedUser } = await import('@edx/frontend-platform/auth');
  const { getConfig } = await import('@edx/frontend-platform');
  const { CustomDesktopNav, CustomMobileNav, UserProfileBootstrap, FloatingButtons } = await import('custom-slot-components');
  const { Helmet } = await import('react-helmet');

  let CustomDesktopNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "learning" === "authoring" ? null : "learning";
      return <CustomDesktopNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let CustomMobileNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "learning" === "authoring" ? null : "learning";
      return <CustomMobileNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let FloatingButtonsWrapper = (props) => {
    const user = getAuthenticatedUser();
    const platformConfig = getConfig();
    return <FloatingButtons
      user={user}
      config={platformConfig}
      backToTop
      {...props}
    />
  };

  const headerScripts = () => {
    return (
      <Helmet>
        <script type="text/javascript">
          {`
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'KTGKT';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);
`}
        </script>
      </Helmet>
    )
  };
             

      const { useParams } = await import('react-router-dom');
      const { useSelector } = await import('react-redux');

      FloatingButtonsWrapper = (props) => {
        const user = getAuthenticatedUser();
        const platformConfig = getConfig();
        const { courseId, sequenceId, unitId } = useParams();
        const reportIssueProps = { courseId, sequenceId, unitId };

        return (
          <FloatingButtons
            user={user}
            config={platformConfig}
            reportIssue={reportIssueProps}
            phoenixAssistant
            {...props}
          />
        );
      };

      // Redefine Desktop for Learning MFE to include selector
      CustomDesktopNavWrapper = (props) => {
        const user = getAuthenticatedUser();
        const platformConfig = getConfig();
        const params = useParams();
        const courseId = params?.courseId ?? null;
        const isStaff = useSelector(state =>
          courseId && state.models?.courseHomeMeta?.[courseId]?.isStaff
        );

        return <CustomDesktopNav 
          user={user}
          config={platformConfig}
          courseId={courseId}
          isStaff={!!isStaff}
          alertSurface="learning"
          {...props}
        />;
      };

      CustomMobileNavWrapper = (props) => {
        const user = getAuthenticatedUser();
        const platformConfig = getConfig();
        const params = useParams();
        const courseId = params?.courseId ?? null;
        const isStaff = useSelector(state =>
          courseId && state.models?.courseHomeMeta?.[courseId]?.isStaff
        );

        return <CustomMobileNav 
          user={user}
          config={platformConfig}
          courseId={courseId}
          isStaff={!!isStaff}
          alertSurface="learning"
          {...props}
        />;
      };
          
      addPlugins(config, 'desktop_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_desktop",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_desktop_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomDesktopNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'mobile_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_mobile",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_mobile_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomMobileNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'helmet_slot', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'helmet_slot',
              type: DIRECT_PLUGIN,
              RenderWidget: headerScripts,
            },
          }
          ]);
    }
    if (process.env.APP_ID == 'profile') {
  const { getAuthenticatedUser } = await import('@edx/frontend-platform/auth');
  const { getConfig } = await import('@edx/frontend-platform');
  const { CustomDesktopNav, CustomMobileNav, UserProfileBootstrap, FloatingButtons } = await import('custom-slot-components');
  const { Helmet } = await import('react-helmet');

  let CustomDesktopNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "profile" === "authoring" ? null : "profile";
      return <CustomDesktopNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let CustomMobileNavWrapper = (props) => {
      const user = getAuthenticatedUser();
      const platformConfig = getConfig();
      const alertSurface = "profile" === "authoring" ? null : "profile";
      return <CustomMobileNav user={user} config={platformConfig} alertSurface={alertSurface} {...props} />;
  };

  let FloatingButtonsWrapper = (props) => {
    const user = getAuthenticatedUser();
    const platformConfig = getConfig();
    return <FloatingButtons
      user={user}
      config={platformConfig}
      backToTop
      {...props}
    />
  };

  const headerScripts = () => {
    return (
      <Helmet>
        <script type="text/javascript">
          {`
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'KTGKT';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);
`}
        </script>
      </Helmet>
    )
  };
             
      addPlugins(config, 'desktop_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_desktop",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_desktop_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomDesktopNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'mobile_header_slot', [
        {
            // Run the profile bootstrap ASAP so useUopxProfile has data
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: "uopx_profile_bootstrap_mobile",
              type: DIRECT_PLUGIN,
              RenderWidget: UserProfileBootstrap,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents'
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'custom_mobile_header_component',
              type: DIRECT_PLUGIN,
              RenderWidget: CustomMobileNavWrapper,
            },
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'floating_buttons_component',
              type: DIRECT_PLUGIN,
              RenderWidget: FloatingButtonsWrapper,
            },
        }
        ]);
      addPlugins(config, 'helmet_slot', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
              id: 'helmet_slot',
              type: DIRECT_PLUGIN,
              RenderWidget: headerScripts,
            },
          }
          ]);
    }
    if (process.env.APP_ID == 'survey-data') {
    }
  } catch { }

  return config;
}

export default setConfig;